/**
 * Environment Ambient Audio System for Counter Pump.
 *
 * Synthesizes all ambient audio layers using the Web Audio API — no external
 * audio files.  Layers include wind, industrial hum, birds, water drip, and
 * electrical buzz.  Map areas use presets that activate specific layer
 * combinations, with smooth crossfades between presets.
 *
 * A-M-001: ambient preset auto-tracks the player's current PVS zone.
 * `update(dt)` polls the PVS module's `getCurrentZone()`/`getZoneLabel()`
 * every frame and crossfades to the zone's preset on label change. The
 * map module is treated as a one-way read-only dependency.
 */

import { getCurrentZone, getZoneLabel } from '../map/occlusion';

// ── Types ───────────────────────────────────────────────────────────────────

export type AmbientLayerName =
  | 'wind'
  | 'industrial_hum'
  | 'birds'
  | 'electrical_buzz'
  | 'water_drip';

export type AmbientPresetName = 'outdoor' | 'indoor' | 'underground' | 'open';

/** Preset definitions: which layers are active for each area type. */
const PRESETS: Record<AmbientPresetName, AmbientLayerName[]> = {
  outdoor: ['wind', 'birds'],
  indoor: ['industrial_hum', 'electrical_buzz'],
  underground: ['water_drip', 'industrial_hum'],
  open: ['wind'],
};

/** Volume targets per layer (relative to master). */
const LAYER_VOLUMES: Record<AmbientLayerName, number> = {
  wind: 0.15,
  industrial_hum: 0.08,
  birds: 0.12,
  electrical_buzz: 0.04,
  water_drip: 0.10,
};

/** Crossfade duration in seconds. */
const CROSSFADE_TIME = 2.0;

/**
 * A-M-001: zone-label → preset mapping. Labels match the VisibilityZone
 * `label` field in client/src/map/occlusion.ts. Unknown labels fall back to
 * 'outdoor' so a new map gets a sensible default until its zones are added.
 *
 * de_outpost_v6 zone reference (occlusion.ts:36-97):
 *   • Outdoor: T Spawn, Long A, Mid Courtyard, Short A, CT Spawn, A Site Upper
 *   • Indoor:  A Site Lower (Command Center), B Site Floor (Warehouse),
 *              B Site Catwalk (Warehouse Upper)
 *   • Underground: B Tunnels, CT-A Tunnel, Drainage Channel
 */
const ZONE_LABEL_TO_PRESET: Record<string, AmbientPresetName> = {
  // Outdoor — yards, courtyards, long open lanes
  'T Spawn / Staging Yard': 'outdoor',
  'Long A': 'outdoor',
  'Mid Courtyard': 'outdoor',
  'Short A Connector': 'outdoor',
  'CT Spawn / HQ Compound': 'outdoor',
  'A Site Upper (Balcony / Roof)': 'outdoor',
  // Indoor — enclosed multi-storey hero buildings
  'A Site Lower (Command Center)': 'indoor',
  'B Site Floor (Warehouse)': 'indoor',
  'B Site Catwalk (Warehouse Upper)': 'indoor',
  // Underground — tunnels and drainage
  'B Tunnels': 'underground',
  'CT-A Tunnel': 'underground',
  'Drainage Channel': 'underground',
};

// ── Layer state ─────────────────────────────────────────────────────────────

interface LayerNodes {
  oscillators: OscillatorNode[];
  gains: GainNode[];
  filters: BiquadFilterNode[];
  bufferSources: AudioBufferSourceNode[];
  timers: ReturnType<typeof setTimeout>[];
  /** The layer's output gain node (before master). */
  outputGain: GainNode;
}

function emptyLayerNodes(outputGain: GainNode): LayerNodes {
  return {
    oscillators: [],
    gains: [outputGain],
    filters: [],
    bufferSources: [],
    timers: [],
    outputGain,
  };
}

// ── AmbientAudioSystem ──────────────────────────────────────────────────────

export class AmbientAudioSystem {
  private ctx: AudioContext;
  private masterGain: GainNode;
  private masterVolume = 1.0;
  private currentPreset: AmbientPresetName | null = null;
  private disposed = false;

  /** Active layers and their Web Audio nodes. */
  private layers = new Map<AmbientLayerName, LayerNodes>();

  /** Layers currently fading out (will be cleaned up after fade). */
  private fadingOut: { nodes: LayerNodes; remaining: number }[] = [];

  constructor() {
    const AudioCtx =
      (globalThis as any).AudioContext ?? (globalThis as any).webkitAudioContext;
    this.ctx = new AudioCtx();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.masterVolume;
    this.masterGain.connect(this.ctx.destination);
  }

  // ── Public API: queries ──────────────────────────────────────────────

  getCurrentPreset(): AmbientPresetName | null {
    return this.currentPreset;
  }

  getMasterVolume(): number {
    return this.masterVolume;
  }

  isLayerActive(name: AmbientLayerName): boolean {
    return this.layers.has(name);
  }

  // ── Public API: volume ───────────────────────────────────────────────

  setMasterVolume(v: number): void {
    this.masterVolume = Math.max(0, Math.min(1, v));
    try {
      this.masterGain.gain.setValueAtTime(this.masterVolume, this.ctx.currentTime);
    } catch {
      this.masterGain.gain.value = this.masterVolume;
    }
  }

  // ── Public API: presets ──────────────────────────────────────────────

  /**
   * Resume the underlying AudioContext. Mobile Safari starts the context
   * suspended until a user gesture is processed — scheduled oscillators
   * silently pile up without playing. Call this on first gesture; safe
   * to call repeatedly.
   */
  resume(): Promise<void> {
    try {
      if (this.ctx.state === 'suspended') {
        return this.ctx.resume();
      }
    } catch { /* context closed */ }
    return Promise.resolve();
  }

  private ensureRunning(): void {
    try {
      if (this.ctx.state === 'suspended') {
        this.ctx.resume().catch(() => { /* ignore */ });
      }
    } catch { /* context closed */ }
  }

  /**
   * Transition to a new ambient preset.  Crossfades over CROSSFADE_TIME.
   * Pass `null` to silence all layers.
   */
  setPreset(name: AmbientPresetName | null): void {
    if (this.disposed) return;

    if (name === this.currentPreset) return;

    if (name !== null) this.ensureRunning();

    // Fade out all current layers
    for (const [layerName, nodes] of this.layers) {
      this.fadeOutLayer(nodes);
      this.layers.delete(layerName);
    }

    this.currentPreset = name;

    if (name === null) return;

    // Activate new layers
    const layerNames = PRESETS[name];
    for (const layerName of layerNames) {
      this.activateLayer(layerName);
    }
  }

  /**
   * A-M-001: switch the ambient preset based on the player's current PVS
   * zone label. Caller passes the result of `getZoneLabel(getCurrentZone())`
   * from `client/src/map/occlusion.ts`. Pass `null` (or an empty/unknown
   * label) to keep the current preset — used when the player is outside
   * any defined zone (loading, between zones).
   *
   * No-op when the label maps to the already-active preset, so this is
   * safe to call every frame.
   */
  updateForZoneLabel(label: string | null | undefined): void {
    if (this.disposed) return;
    if (!label) return;
    const next = ZONE_LABEL_TO_PRESET[label];
    if (!next) return;
    if (next === this.currentPreset) return;
    this.setPreset(next);
  }

  // ── Public API: individual layer control ─────────────────────────────

  setLayerEnabled(name: AmbientLayerName, enabled: boolean): void {
    if (this.disposed) return;

    const isActive = this.layers.has(name);
    if (enabled && !isActive) {
      this.ensureRunning();
      this.activateLayer(name);
    } else if (!enabled && isActive) {
      const nodes = this.layers.get(name)!;
      this.fadeOutLayer(nodes);
      this.layers.delete(name);
    }
  }

  // ── Public API: update loop ──────────────────────────────────────────

  /**
   * Called every frame.  Advances crossfade timers and cleans up
   * finished fade-outs.
   *
   * A-M-001: also polls the PVS zone label via the lazy
   * `pvsZoneLabelProvider` (set with `setPVSZoneLabelProvider`) and
   * switches preset whenever the zone changes. Skipped when no provider
   * is wired — keeps ambient usable in tests and headless contexts.
   */
  update(dt: number): void {
    if (this.disposed) return;

    // Process fading-out layers
    for (let i = this.fadingOut.length - 1; i >= 0; i--) {
      const entry = this.fadingOut[i];
      entry.remaining -= dt;
      if (entry.remaining <= 0) {
        this.cleanupLayerNodes(entry.nodes);
        this.fadingOut.splice(i, 1);
      }
    }

    // A-M-001: zone-label poll. Cheap — getCurrentZone() is a module-local
    // integer read and getZoneLabel() is an array index. The PVS engine
    // only changes `currentZone` on a cross-zone move, so most frames the
    // label is unchanged and we early-return on the lastZoneLabel guard.
    if (!this.pvsZoneTrackingDisabled) {
      const zoneIdx = getCurrentZone();
      // -1 (outside any zone) or invalid index: keep current preset.
      if (zoneIdx >= 0) {
        const label = getZoneLabel(zoneIdx);
        if (label && label !== this.lastZoneLabel) {
          this.lastZoneLabel = label;
          this.updateForZoneLabel(label);
        }
      }
    }
  }

  /**
   * A-M-001: opt-out for callers that want full manual preset control
   * (e.g. cinematic replay scenes, unit tests). When true, `update(dt)`
   * skips the PVS zone-label poll.
   */
  setPVSZoneTrackingEnabled(enabled: boolean): void {
    this.pvsZoneTrackingDisabled = !enabled;
    if (!enabled) this.lastZoneLabel = null;
  }

  private pvsZoneTrackingDisabled = false;
  private lastZoneLabel: string | null = null;

  // ── Public API: dispose ──────────────────────────────────────────────

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    // Clean up all active layers
    for (const [, nodes] of this.layers) {
      this.cleanupLayerNodes(nodes);
    }
    this.layers.clear();

    // Clean up fading layers
    for (const entry of this.fadingOut) {
      this.cleanupLayerNodes(entry.nodes);
    }
    this.fadingOut = [];

    this.currentPreset = null;

    try { this.masterGain.disconnect(); } catch { /* already disconnected */ }
    try { this.ctx.close(); } catch { /* already closed */ }
  }

  // ── Internal: layer lifecycle ────────────────────────────────────────

  private activateLayer(name: AmbientLayerName): void {
    const outputGain = this.ctx.createGain();
    const targetVol = LAYER_VOLUMES[name];

    // Fade in over CROSSFADE_TIME
    const now = this.ctx.currentTime;
    outputGain.gain.setValueAtTime(0, now);
    outputGain.gain.linearRampToValueAtTime(targetVol, now + CROSSFADE_TIME);

    outputGain.connect(this.masterGain);

    const nodes = emptyLayerNodes(outputGain);

    switch (name) {
      case 'wind':
        this.synthWind(nodes);
        break;
      case 'industrial_hum':
        this.synthIndustrialHum(nodes);
        break;
      case 'birds':
        this.synthBirds(nodes);
        break;
      case 'electrical_buzz':
        this.synthElectricalBuzz(nodes);
        break;
      case 'water_drip':
        this.synthWaterDrip(nodes);
        break;
    }

    this.layers.set(name, nodes);
  }

  private fadeOutLayer(nodes: LayerNodes): void {
    const now = this.ctx.currentTime;
    try {
      nodes.outputGain.gain.cancelScheduledValues(now);
      nodes.outputGain.gain.setValueAtTime(nodes.outputGain.gain.value, now);
      nodes.outputGain.gain.linearRampToValueAtTime(0, now + CROSSFADE_TIME);
    } catch { /* context may be closed */ }

    this.fadingOut.push({ nodes, remaining: CROSSFADE_TIME });
  }

  private cleanupLayerNodes(nodes: LayerNodes): void {
    // Clear timers
    for (const t of nodes.timers) clearTimeout(t);
    nodes.timers.length = 0;

    // Stop oscillators
    for (const osc of nodes.oscillators) {
      try { osc.stop(); } catch { /* may already be stopped */ }
      try { osc.disconnect(); } catch { /* no-op */ }
    }

    // Stop buffer sources
    for (const bs of nodes.bufferSources) {
      try { bs.stop(); } catch { /* may already be stopped */ }
      try { bs.disconnect(); } catch { /* no-op */ }
    }

    // Disconnect gains and filters
    for (const g of nodes.gains) {
      try { g.disconnect(); } catch { /* no-op */ }
    }
    for (const f of nodes.filters) {
      try { f.disconnect(); } catch { /* no-op */ }
    }
  }

  // ── Synth: Wind ──────────────────────────────────────────────────────
  // Filtered white noise (bandpass 200-800Hz) with slow LFO modulating
  // gain for a breathing effect.

  private synthWind(nodes: LayerNodes): void {
    const now = this.ctx.currentTime;

    // Create white noise buffer source
    const bufferLength = this.ctx.sampleRate * 4; // 4 second loop
    const noiseBuffer = this.ctx.createBuffer(1, bufferLength, this.ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferLength; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noiseSource = this.ctx.createBufferSource();
    noiseSource.buffer = noiseBuffer;
    noiseSource.loop = true;
    nodes.bufferSources.push(noiseSource);

    // Bandpass filter centered at 500Hz
    const bp = this.ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 500;
    bp.Q.value = 0.8;
    nodes.filters.push(bp);

    // LFO for breathing effect (slow gain modulation)
    const lfo = this.ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.15; // very slow: ~7s cycle
    nodes.oscillators.push(lfo);

    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 0.3; // modulation depth
    nodes.gains.push(lfoGain);

    const windGain = this.ctx.createGain();
    windGain.gain.value = 0.7; // base level
    nodes.gains.push(windGain);

    // Connect: noiseSource → bp → windGain → outputGain
    // LFO → lfoGain → windGain.gain (modulates)
    noiseSource.connect(bp);
    bp.connect(windGain);
    windGain.connect(nodes.outputGain);

    lfo.connect(lfoGain);
    lfoGain.connect(windGain.gain);

    noiseSource.start(now);
    lfo.start(now);
  }

  // ── Synth: Industrial Hum ────────────────────────────────────────────
  // Low oscillator: 50Hz sawtooth + 100Hz sine, very low gain, subtle
  // machinery drone.

  private synthIndustrialHum(nodes: LayerNodes): void {
    const now = this.ctx.currentTime;

    // 50Hz sawtooth — machinery fundamental
    const saw50 = this.ctx.createOscillator();
    saw50.type = 'sawtooth';
    saw50.frequency.value = 50;
    nodes.oscillators.push(saw50);

    const saw50Gain = this.ctx.createGain();
    saw50Gain.gain.value = 0.6;
    nodes.gains.push(saw50Gain);

    // 100Hz sine — first harmonic
    const sine100 = this.ctx.createOscillator();
    sine100.type = 'sine';
    sine100.frequency.value = 100;
    nodes.oscillators.push(sine100);

    const sine100Gain = this.ctx.createGain();
    sine100Gain.gain.value = 0.4;
    nodes.gains.push(sine100Gain);

    // Low-pass filter to tame harshness
    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 300;
    lp.Q.value = 1;
    nodes.filters.push(lp);

    // Connect: oscillators → gains → lp → outputGain
    saw50.connect(saw50Gain);
    saw50Gain.connect(lp);

    sine100.connect(sine100Gain);
    sine100Gain.connect(lp);

    lp.connect(nodes.outputGain);

    saw50.start(now);
    sine100.start(now);
  }

  // ── Synth: Birds ─────────────────────────────────────────────────────
  // Occasional high-pitched chirps: random 2-4kHz sine bursts,
  // 50-100ms duration, random intervals 3-8s.

  private synthBirds(nodes: LayerNodes): void {
    const scheduleChirp = () => {
      if (this.disposed || !this.layers.has('birds')) return;

      // Random chirp frequency 2000-4000 Hz
      const freq = 2000 + Math.random() * 2000;
      // Random duration 50-100ms
      const dur = 0.05 + Math.random() * 0.05;

      const now = this.ctx.currentTime;

      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      // Slight pitch bend for realism
      osc.frequency.setValueAtTime(freq, now);
      osc.frequency.linearRampToValueAtTime(freq * 0.85, now + dur);
      nodes.oscillators.push(osc);

      const chirpGain = this.ctx.createGain();
      chirpGain.gain.setValueAtTime(0, now);
      chirpGain.gain.linearRampToValueAtTime(0.8, now + dur * 0.1);
      chirpGain.gain.linearRampToValueAtTime(0, now + dur);
      nodes.gains.push(chirpGain);

      osc.connect(chirpGain);
      chirpGain.connect(nodes.outputGain);

      osc.start(now);
      osc.stop(now + dur + 0.01);

      // One-shot: drop refs from the layer arrays once the node finishes so
      // long sessions don't accumulate dead OscillatorNode/GainNode entries.
      osc.onended = () => {
        try { osc.disconnect(); } catch { /* no-op */ }
        try { chirpGain.disconnect(); } catch { /* no-op */ }
        const oi = nodes.oscillators.indexOf(osc);
        if (oi !== -1) nodes.oscillators.splice(oi, 1);
        const gi = nodes.gains.indexOf(chirpGain);
        if (gi !== -1) nodes.gains.splice(gi, 1);
      };

      // Schedule next chirp: 3-8s
      const nextDelay = 3000 + Math.random() * 5000;
      const timer = setTimeout(scheduleChirp, nextDelay);
      nodes.timers.push(timer);
    };

    // First chirp after 1-3s
    const initialDelay = 1000 + Math.random() * 2000;
    const timer = setTimeout(scheduleChirp, initialDelay);
    nodes.timers.push(timer);
  }

  // ── Synth: Water Drip ────────────────────────────────────────────────
  // Random high-freq click + decay (2-3kHz, very short), intervals
  // 4-12s, with reverb.

  private synthWaterDrip(nodes: LayerNodes): void {
    const scheduleDrip = () => {
      if (this.disposed || !this.layers.has('water_drip')) return;

      const freq = 2000 + Math.random() * 1000;
      const dur = 0.08; // very short

      const now = this.ctx.currentTime;

      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      // Downward pitch for drip character
      osc.frequency.setValueAtTime(freq, now);
      osc.frequency.linearRampToValueAtTime(freq * 0.5, now + dur);
      nodes.oscillators.push(osc);

      const dripGain = this.ctx.createGain();
      // Sharp attack, exponential-ish decay
      dripGain.gain.setValueAtTime(0.9, now);
      dripGain.gain.linearRampToValueAtTime(0, now + dur);
      nodes.gains.push(dripGain);

      osc.connect(dripGain);
      dripGain.connect(nodes.outputGain);

      osc.start(now);
      osc.stop(now + dur + 0.01);

      // One-shot: drop refs when finished to keep arrays bounded across long sessions.
      osc.onended = () => {
        try { osc.disconnect(); } catch { /* no-op */ }
        try { dripGain.disconnect(); } catch { /* no-op */ }
        const oi = nodes.oscillators.indexOf(osc);
        if (oi !== -1) nodes.oscillators.splice(oi, 1);
        const gi = nodes.gains.indexOf(dripGain);
        if (gi !== -1) nodes.gains.splice(gi, 1);
      };

      // Schedule next drip: 4-12s
      const nextDelay = 4000 + Math.random() * 8000;
      const timer = setTimeout(scheduleDrip, nextDelay);
      nodes.timers.push(timer);
    };

    // First drip after 2-5s
    const initialDelay = 2000 + Math.random() * 3000;
    const timer = setTimeout(scheduleDrip, initialDelay);
    nodes.timers.push(timer);
  }

  // ── Synth: Electrical Buzz ───────────────────────────────────────────
  // 60Hz sine + harmonics (120Hz, 180Hz), very quiet, for indoor areas.

  private synthElectricalBuzz(nodes: LayerNodes): void {
    const now = this.ctx.currentTime;

    // 60Hz fundamental
    const fund = this.ctx.createOscillator();
    fund.type = 'sine';
    fund.frequency.value = 60;
    nodes.oscillators.push(fund);

    const fundGain = this.ctx.createGain();
    fundGain.gain.value = 0.5;
    nodes.gains.push(fundGain);

    // 120Hz 2nd harmonic
    const harm2 = this.ctx.createOscillator();
    harm2.type = 'sine';
    harm2.frequency.value = 120;
    nodes.oscillators.push(harm2);

    const harm2Gain = this.ctx.createGain();
    harm2Gain.gain.value = 0.3;
    nodes.gains.push(harm2Gain);

    // 180Hz 3rd harmonic
    const harm3 = this.ctx.createOscillator();
    harm3.type = 'sine';
    harm3.frequency.value = 180;
    nodes.oscillators.push(harm3);

    const harm3Gain = this.ctx.createGain();
    harm3Gain.gain.value = 0.15;
    nodes.gains.push(harm3Gain);

    // Connect: oscillators → gains → outputGain
    fund.connect(fundGain);
    fundGain.connect(nodes.outputGain);

    harm2.connect(harm2Gain);
    harm2Gain.connect(nodes.outputGain);

    harm3.connect(harm3Gain);
    harm3Gain.connect(nodes.outputGain);

    fund.start(now);
    harm2.start(now);
    harm3.start(now);
  }
}

/** Lazy singleton ambient audio instance — deferred to avoid AudioContext in test envs. */
let _ambientAudio: AmbientAudioSystem | null = null;
export const ambientAudio: AmbientAudioSystem = new Proxy({} as AmbientAudioSystem, {
  get(_target, prop) {
    if (!_ambientAudio) _ambientAudio = new AmbientAudioSystem();
    return (_ambientAudio as any)[prop];
  },
});

